import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { StaticContext } from 'react-router';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';

import type { W_Equipment_FindAllForCompanyList_VM } from 'ab-api/web/equipment/findAllForCompanyList';
import type { W_ReportType_Create_RM, W_ReportType_Create_RM_ReportBlockField, W_ReportType_Create_RM_ReportTypeBlock } from 'ab-api/web/reportType/create';

import type ReportBlockMapVM from 'ab-viewModels/reportBlock/reportBlockMap.viewModel';
import type ReportTypeLocationVM from 'ab-viewModels/reportType/locationState.viewModel';
import type { ReportTypeVM } from 'ab-viewModels/reportType/reportType.viewModel';

import { flattenElements } from '@acceligentllc/shared/utils/array';

import type { RootState } from 'af-reducers';

import CLIENT from 'af-constants/routes/client';

import Breadcrumbs from 'af-components/Breadcrumbs';

import * as EquipmentActions from 'af-actions/equipment';
import * as ReportBlockActions from 'af-actions/reportBlock';
import * as ReportTypeActions from 'af-actions/reportType';

import { ReportBlockFieldFormModel } from '../../Shared/formModel';
import { ReportTypePreviewHighlightContext, useReportTypePreviewHighlightContextState } from '../Context/ReportTypePreviewHighlight';
import type { RequestModelMapperContext } from '../CustomTypeForm';
import ReportTypeForm from '../CustomTypeForm';
import { fromLocationStateToVM } from './helpers';

interface LocationState {
	orgAlias: string;
	reportType: ReportTypeLocationVM;
}

type OwnProps = CustomRouteComponentProps<void, StaticContext, LocationState>;

type Props = OwnProps & ConnectedProps<typeof connector>;

const getFieldFormModelToRequestModelMapper = (reportFieldsByIdMap: { [id: string]: ReportBlockFieldFormModel; }) => {
	return (id: string, index: number): W_ReportType_Create_RM_ReportBlockField => {
		const field = reportFieldsByIdMap[id];
		return ReportBlockFieldFormModel.toCreateReportTypeReportBlockFieldRM(field, index);
	};
};

/** Maps report type block Form Models to RMs and adds calculation field options */
const getReportTypeBlockFormModelToRequestModelMapper = (
	updateIndex: boolean,
	reportTypeBlocksByIdMap: RequestModelMapperContext['reportTypeBlocksByIdMap'],
	reportBlocksByIdMap: RequestModelMapperContext['reportBlocksByIdMap'],
	reportFieldsByIdMap: RequestModelMapperContext['reportFieldsByIdMap']
) => {
	return (id: string, index: Nullable<number>): W_ReportType_Create_RM_ReportTypeBlock => {
		const fieldMapper = getFieldFormModelToRequestModelMapper(reportFieldsByIdMap);
		const typeBlock = reportTypeBlocksByIdMap[id];
		const reportBlock = reportBlocksByIdMap[typeBlock.reportBlockVirtualId];

		return {
			...typeBlock,
			reportTypeId: typeBlock.reportTypeId ?? undefined,
			reportBlockId: typeBlock.reportBlockId,
			index: updateIndex ? index : null,
			reportBlock: {
				...reportBlocksByIdMap[typeBlock.reportBlockVirtualId],
				reportBlockFields: (reportBlock.reportBlockFieldIds ?? []).map(fieldMapper),
			},
		};
	};
};

const toRequestModel = (context: RequestModelMapperContext): W_ReportType_Create_RM => {
	const {
		billableWorks,
		primaryTypeBlockIds,
		secondaryTypeBlockIds,
		primaryTypeMainBlockId,
		secondaryTypeMainBlockId,
		upperTotalBlockId,
		lowerTotalBlockId,
		reportTypeName,
		reportTypeDescription,
		reportTypeBlocksByIdMap,
		reportBlocksByIdMap,
		reportFieldsByIdMap,
	} = context;

	const reportTypeBlocks: W_ReportType_Create_RM_ReportTypeBlock[] = [];

	// Creates Report Type Block VM to RM mappers
	const reportTypeBlocksRMMapper = getReportTypeBlockFormModelToRequestModelMapper(
		true, reportTypeBlocksByIdMap, reportBlocksByIdMap, reportFieldsByIdMap
	);
	const mainReportTypeBlocksRMMapper = getReportTypeBlockFormModelToRequestModelMapper(
		false, reportTypeBlocksByIdMap, reportBlocksByIdMap, reportFieldsByIdMap
	);

	const primaryIds = flattenElements(primaryTypeMainBlockId, primaryTypeBlockIds);
	const secondaryIds = flattenElements(secondaryTypeMainBlockId, secondaryTypeBlockIds);

	reportTypeBlocks.push(...primaryIds.map(reportTypeBlocksRMMapper));
	reportTypeBlocks.push(...secondaryIds.map(reportTypeBlocksRMMapper));

	if (upperTotalBlockId) {
		reportTypeBlocks.push(mainReportTypeBlocksRMMapper(upperTotalBlockId, null));
	}
	if (lowerTotalBlockId) {
		reportTypeBlocks.push(mainReportTypeBlocksRMMapper(lowerTotalBlockId, null));
	}

	return {
		name: reportTypeName,
		reportTypeBlocks,
		description: reportTypeDescription,
		billableWorks,
	};
};

const CreateReportType: React.FC<Props> = (props: Props) => {
	const {
		location: { state: { orgAlias } },
		create,
		history,
		companyName,
		fetchReportBlocks,
		fetchEquipmentList,
	} = props;

	const reportTypePreviewHighlight = useReportTypePreviewHighlightContextState();

	const [blocks, setBlocks] = React.useState<ReportBlockMapVM>({ blockMap: {}, fieldMap: {} });
	const [reportType] = React.useState<ReportTypeVM>(fromLocationStateToVM(props.location.state.reportType));
	const [listOfEquipment, setListOfEquipment] = React.useState<Nullable<W_Equipment_FindAllForCompanyList_VM>>(null);
	const [fetchingBlocks, setFetchingBlock] = React.useState(true);

	const isSubmittingRef = React.useRef(false);

	React.useEffect(() => {
		async function getBlocks() {
			try {
				setFetchingBlock(true);
				const _blocks = await fetchReportBlocks();
				setBlocks(_blocks);
			} finally {
				setFetchingBlock(false);
			}
		}
		getBlocks();
	}, [fetchReportBlocks]);

	const onCreate = React.useCallback(async (rm: W_ReportType_Create_RM) => {
		if (isSubmittingRef.current) {
			return;
		}

		isSubmittingRef.current = true;

		try {
			await create(rm);
			history.push(CLIENT.COMPANY.SETTINGS.REPORT.TABLE(orgAlias, companyName));
		} finally {
			isSubmittingRef.current = false;
		}
	}, [companyName, create, history, orgAlias]);

	const fetchListOfEquipment = React.useCallback(async () => {
		const equipment = await fetchEquipmentList('');
		setListOfEquipment(equipment);
	}, [fetchEquipmentList]);

	const breadcrumbs = React.useMemo(() => [
		{ label: 'Report Type', url: CLIENT.COMPANY.SETTINGS.REPORT.TABLE(orgAlias, companyName) },
		{ label: 'Create' },
	], [companyName, orgAlias]);

	return (
		<ReportTypePreviewHighlightContext.Provider value={reportTypePreviewHighlight}>
			<div className="form-segment">
				<Breadcrumbs
					items={breadcrumbs}
				/>
				<ReportTypeForm<W_ReportType_Create_RM>
					fetchListOfEquipment={fetchListOfEquipment}
					listOfEquipment={listOfEquipment}
					loading={fetchingBlocks}
					onSubmit={onCreate}
					reportBlockFieldsMap={blocks.fieldMap}
					reportBlocksMap={blocks.blockMap}
					reportType={reportType}
					requestModelMapper={toRequestModel}
				/>
			</div>
		</ReportTypePreviewHighlightContext.Provider>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		fetchReportBlocks: ReportBlockActions.findList,
		create: ReportTypeActions.createCustom,
		fetchEquipmentList: EquipmentActions.findAllForCompanyList,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	React.memo,
	connector
);

export default enhance(CreateReportType);
